const { getInsertProperties, queryPaginationTransform } = require('../tools/sql-tools')

// discount_item values sit in a VALUES list rather than an insert target, so
// postgres cannot infer their types from the column - cast each one explicitly.
const ITEM_COLUMN_TYPES = {
  product_sku_id: 'text',
  location_id: 'text',
  market_id: 'text',
  order_id: 'text',
  organisation_id: 'uuid'
}

// Sort keys a caller may ask for, mapped to their column. Order by cannot be
// parameterised, so anything outside this map is ignored rather than interpolated.
const SORTABLE_COLUMNS = {
  status: 'status',
  active: 'is_active',
  created_at: 'created_at',
  updated_at: 'updated_at',
  level: 'application_level',
  start_date: 'start_date',
  end_date: 'end_date'
}

const DEFAULT_SORT_COLUMN = 'updated_at'

// updated_at always follows the chosen column, then id breaks any remaining tie: without
// a unique last term a row can repeat on one page and vanish from the next.
const getOrderByClause = (sort = {}) => {
  const column = SORTABLE_COLUMNS[sort.field] || DEFAULT_SORT_COLUMN
  const direction = sort.direction === 'asc' ? 'asc' : 'desc'
  const terms = [`${column} ${direction}`]
  if (column !== 'updated_at') {
    terms.push('updated_at desc')
  }
  terms.push('id desc')
  return `order by ${terms.join(', ')}`
}

class DiscountPgAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
    this.LIMIT = 50
  }

  async getProductSkus (skuIds) {
    const { rows } = await this.pgConnection.query(`
      select 
        old_sku_id as "id"
      from avocado.data_productsku
      where old_sku_id = any($1)
    `, [skuIds])
    return rows.map(sku => sku.id)
  }

  async getLocations (locationIds) {
    const { rows } = await this.pgConnection.query(`
      select
        uuid
      from avocado.data_location
      where uuid = any($1)
    `, [locationIds])
    return rows.map(loc => loc.uuid)
  }

  async getMarkets (marketIds) {
    const { rows } = await this.pgConnection.query(`
       select
         id
        from avocado.data_market
        where id = any($1)
    `, [marketIds])
    return rows.map(market => market.id)
  }

  async getOrders (orderIds) {
    const { rows } = await this.pgConnection.query(`
      select
        order_id as "orderId"
      from avocado.data_order
      where order_id = any($1)  
    `, [orderIds])
    return rows.map(order => order.orderId)
  }

  async getOrganisations (organisationIds) {
    const { rows } = await this.pgConnection.query(`
      select
        id
      from avocado.data_organisation
      where id = any($1)  
    `, [organisationIds])
    return rows.map(org => org.id)
  }

  async create (discountRow, items) {
    if (!items || items.length === 0) {
      const error = new Error('A discount requires at least one item')
      error.status = 400
      throw error
    }

    const discount = getInsertProperties([discountRow])
    const item = getInsertProperties(items, null, discount.values.length, ITEM_COLUMN_TYPES)

    const { rows } = await this.pgConnection.query(`
      with new_discount as (
        insert into avocado.discount (${discount.columns})
        values ${discount.placeholders}
        returning *
      ), new_items as (
        insert into avocado.discount_item (discount_id, ${item.columns})
        select new_discount.id, item.*
        from new_discount
        cross join (values ${item.placeholders}) as item(${item.columns})
        returning *
      )
      select
        to_jsonb(new_discount) as discount,
        (select coalesce(jsonb_agg(to_jsonb(new_items)), '[]'::jsonb) from new_items) as items
      from new_discount
    `, [...discount.values, ...item.values])

    return rows[0]
  }

  async get (id) {
    const {rows} = await this.pgConnection.query(`
      with discount as (
        select
          *
        from avocado.discount d
        where d.id = $1
      ),
      items as (
        select
          di.id,
          di.discount_id,
          case
            when d.application_level = 'location' then l."uuid"::text
            when d.application_level = 'market' then m.id::text
            when d.application_level = 'product' then ps.old_sku_id::text
            when d.application_level = 'organisation' then org.id::text
            when d.application_level = 'order' then o.order_id:: text
          end as item_id,
          case
            when d.application_level = 'location' then l.name_of_pharmacy
            when d.application_level = 'market' then m."name"
            when d.application_level = 'product' then ps.display_name
            when d.application_level = 'organisation' then org."name" 
            when d.application_level = 'order' then o.order_id 
          end as item_name,
          case when d.application_level = 'product' then di.location_id end as location_id,
          case when d.application_level = 'product' then l.name_of_pharmacy end as location_name,
          di.created_at,
          di.updated_at
        from discount d
        left join avocado.discount_item di on d.id = di.discount_id
        left join avocado.data_location l on l.uuid = di.location_id 
        left join avocado.data_organisation org on org.id = di.organisation_id 
        left join avocado.data_productsku ps on ps.old_sku_id = di.product_sku_id 
        left join avocado.data_market m on m.id = di.market_id
        left join avocado.data_order o on o.order_id = di.order_id
      )
      select 
        d.*,
        coalesce(
          (
            select jsonb_agg(i)
            from (
              select
                i2.id,
                i2.item_id,
                i2.item_name,
                i2.location_id,
                i2.location_name,
                i2.created_at,
                i2.updated_at
              from items i2
              where i2.discount_id = d.id
             ) i
           ),
          '[]'::jsonb
        ) as items
      from discount d
    `, [id])
    return rows[0]
  }

  async getList (page, limit, sort) {
    const paginatedQuery = queryPaginationTransform({
      baseQuery: `
        with discount as (
          select
            *
          from avocado.discount d
        ),
        items as (
          select
            di.id,
            di.discount_id,
            case
              when d.application_level = 'location' then l."uuid"::text
              when d.application_level = 'market' then m.id::text
              when d.application_level = 'product' then ps.old_sku_id::text
              when d.application_level = 'organisation' then org.id::text
              when d.application_level = 'order' then o.order_id:: text
            end as item_id,
            case
              when d.application_level = 'location' then l.name_of_pharmacy
              when d.application_level = 'market' then m."name"
              when d.application_level = 'product' then ps.display_name
              when d.application_level = 'organisation' then org."name" 
              when d.application_level = 'order' then o.order_id 
            end as item_name,
            case when d.application_level = 'product' then di.location_id end as location_id,
            case when d.application_level = 'product' then l.name_of_pharmacy end as location_name,
            di.created_at,
            di.updated_at
          from discount d
          left join avocado.discount_item di on d.id = di.discount_id
          left join avocado.data_location l on l.uuid = di.location_id 
          left join avocado.data_organisation org on org.id = di.organisation_id 
          left join avocado.data_productsku ps on ps.old_sku_id = di.product_sku_id 
          left join avocado.data_market m on m.id = di.market_id
          left join avocado.data_order o on o.order_id = di.order_id
        ),
        discount_items as (
          select 
            d.*,
            coalesce(
              (
                select jsonb_agg(i)
                from (
                  select
                    i2.id,
                    i2.item_id,
                    i2.item_name,
                    i2.location_id,
                    i2.location_name,
                    i2.created_at,
                    i2.updated_at
                  from items i2
                  where i2.discount_id = d.id
                 ) i
               ),
              '[]'::jsonb
            ) as items
          from discount d
        )
      `,
      cteName: 'discount_items',
      orderByClause: getOrderByClause(sort),
      pageNo: page,
      maxLimit: limit || this.LIMIT
    })

    const { rows: [data] } = await this.pgConnection.query(paginatedQuery)
    return data
  }

  async replaceItems (id, items) {
    const item = getInsertProperties(items, null, 1, ITEM_COLUMN_TYPES)
    const { rows } = await this.pgConnection.query(`
      with removed as (
        delete from avocado.discount_item where discount_id = $1
      )
      insert into avocado.discount_item (discount_id, ${item.columns})
      select $1, item.*
      from (values ${item.placeholders}) as item(${item.columns})
      returning *
    `, [id, ...item.values])
    return rows
  }

  async delete (id) {
    const { rows } = await this.pgConnection.query(`
      delete from avocado.discount
      where id = $1
      returning *
    `, [id])
    return rows[0]
  }

  async update (id, data, status) {
    const columns = Object.keys(data)
    // Identifiers cannot be parameterised, so quote them; an unknown column then
    // fails as a plain postgres error instead of altering the statement.
    const assignments = columns.map(
      (column, index) => `"${column.replace(/"/g, '""')}" = $${index + 3}`
    )

    const { rows } = await this.pgConnection.query(`
      update avocado.discount
      set ${[...assignments, 'updated_at = now()'].join(', ')}
      where id = $1 and status = $2
      returning *
    `, [id, status, ...columns.map(column => data[column])])

    return rows[0]
  }

  async getApplicableDiscounts (locationFsid, orderId) {
    const params = [locationFsid]
    let orderSubQuery = ''
    if (orderId) {
      orderSubQuery = `
        union all

        -- Order discounts
        select
          *
        from avocado.discount_item
        where order_id = $2
      `
      params.push(orderId)
    }
    const { rows } = await this.pgConnection.query(`
      with base_location as (
        select
          fsid,
          uuid as location_id,
          market_id
        from avocado.data_location l
        where l."fsid" = $1
      ),
      applicable_items as (
        -- Location and market discounts
        select
          di.*
        from base_location bl
        inner join avocado.discount_item di on di.location_id = bl.location_id or di.market_id = bl.market_id

        union all

        -- Product discounts not scoped to a location; scoped ones matched above
        select
          di.*
        from base_location bl
        inner join avocado.data_productsku ps on ps.market_id = bl.market_id
        inner join avocado.discount_item di on di.product_sku_id = ps.old_sku_id
        where di.location_id is null
      
        ${orderSubQuery}
      )
      select 
        d.id,
        d.description,
        d.discount_percentage::float as "discountPercentage",
        d.discount_amount::float as "discountAmount",
        d.currency,
        d.application_level,
        d.status,
        d.is_active as "isActive",
        d.start_date as "startDate",
        d.end_date as "endDate",
        coalesce(
            (
              select jsonb_agg(i)
                from (
                  select
                    i2.id,
                    case
                     when d.application_level = 'location' then i2.location_id::text
                     when d.application_level = 'market' then i2.market_id::text
                     when d.application_level = 'product' then concat('product:', i2.product_sku_id)::text
                     when d.application_level = 'organisation' then i2.organisation_id::text
                     when d.application_level = 'order' then i2.order_id:: text
                   end as "itemId",
                   i2.location_id as "locationId"
                  from applicable_items i2
                  where i2.discount_id = d.id
                ) i
              ),
            '[]'::jsonb
        ) as items
      from applicable_items ai
      inner join avocado.discount d on d.id = ai.discount_id
      where d.status = 'approved' and d.is_active is true and now() >= d.start_date and (d.end_date is null or d.end_date > now())
      group by 1
    `, params)
    return rows
  }
}

module.exports = DiscountPgAdapter
